import { Dispatch, SetStateAction } from "react";
import Navigator from './components/Navigation';
import { WorkTitle } from "./workDetailPage/WorkTitle";
import { WorkTextSection } from "./workDetailPage/WorkTextSection";
import { MainImage } from "./workDetailPage/WorkMainImage";
import mainImage from './image/works/work15MainImage.webp';
import NextItem from "./components/NextItem";
import thumbnail16 from './image/workThumbnails/thumbnail16.jpg';
import BackButton from "./components/BackButton";
import { useTranslation } from './hooks/useTranslation';
import { useLanguage } from './contexts/LanguageContext';
import background1 from './image/works/work15background1.webp';
import background1kor from './image/works/work15background1kor.webp';
import { WorkFullImage } from "./workDetailPage/WorkFullImage";
import vision1 from './image/works/work15vision1.webp';
import vision1kor from './image/works/work15vision1kor.webp';
import goal1 from './image/works/work15goal1.webp';
import { WorkImage } from "./workDetailPage/WorkImage";
import WorkImageGroup from "./components/WorkImageGroup";
import research1 from './image/works/work15research1.webp';
import research2 from './image/works/work15research2.webp';
import { Divider } from "@mui/material";
import { WorkVideo } from "./workDetailPage/WorkVideo";
import userflow1 from './image/works/work15flow1.webp';
import userflow2 from './image/works/work15flow2.webp';
import design11 from './image/works/work15design11.webp';
import design12 from './image/works/work15design12.webp';
import prototype1 from './image/works/work15prototype1.webp';
import design21Video from './image/works/work15design21.mov';
import design22 from './image/works/work15design22.webp';
import design23 from './image/works/work15design23.webp';
import design31 from './image/works/work15design31.webp';
import design24Video from './image/works/work15design24.mov';
import imp1 from './image/works/work15implementation1.webp';
import imp2 from './image/works/work15implementation2.webp';
import imp3 from './image/works/work15implementation3.webp';
import imp4 from './image/works/work15implementation4.webp';
import imp5 from './image/works/work15implementation5.webp';
import next1 from './image/works/work15next1.webp';
import next1kor from './image/works/work15next1kor.webp';
import { useMetaTags } from './utils/useMetaTags';
// import design3 from './image/works/work15design3.png';

function Work15(props: {
    activeNav: number, 
    setActiveNav: Dispatch<SetStateAction<number>>,
  }) {
    const { language } = useLanguage();
    
    // 이미지들을 객체로 관리 (kor 버전 포함)
    const images = {
      background1,
      background1kor,
      vision1,
      vision1kor,
      next1,
      next1kor,
      goal1,
      research1,
      research2,
      userflow1,
      userflow2,
      design11,
      design12,
      prototype1,
      design22,
      design23,
      design31,
      imp1,
      imp2,
      imp3,
      imp4,
      imp5,
    };
    
    // 언어에 따라 이미지 선택하는 함수
    const getImage = (imageName: string): string => {
      // 한국어일 때 kor 버전 이미지 찾기
      if (language === 'ko') {
        const korImageName = `${imageName}kor`;
        const korImage = images[korImageName as keyof typeof images];
        
        // kor 버전이 있으면 사용, 없으면 원본 사용
        if (korImage) {
          return korImage;
        }
      }
      
      // 영어이거나 kor 버전이 없으면 원본 이미지 사용
      return images[imageName as keyof typeof images] || '';
    };
    // WorkTitle 수동 번역(ko) 전달용 텍스트
    const titleKo = '새로운 QR오더 솔루션';
    const descriptionKo = '오프라인 매장에서 음식 주문 및 결제를 할 수 있는 QR오더 웹 서비스를 디자인했습니다.';
    const teamKo = '당근';
    const roleKo = '프로젝트 매니징, 제품 및 UXUI 디자인';

    // 영문 기본 텍스트(WorkTitle의 기본값)
    const titleEn = 'New QR Order Solution';
    const descriptionEn = 'QR Designing a Web Service for Restaurant Food Ordering and Payment via QR Code';
    const teamEn = 'Karrot(Daangn)';
    const roleEn = 'Project managing, Product & UXUI Design';

    // 메타태그는 기존처럼 언어에 따라 노출
    const titleText = language === 'ko' ? titleKo : titleEn;
    const descriptionText = language === 'ko' ? descriptionKo : descriptionEn;
    
    useMetaTags({
      title: `${titleText} - Lynkim`,
      description: descriptionText,
      image: '/ogimage.png',
    });
    const teamText = language === 'ko' ? teamKo : teamEn;
    const roleText = language === 'ko' ? roleKo : roleEn;

    // 그 외 텍스트는 기존 훅 유지
    const nextProjectTranslation = useTranslation('Obtaining Consent for Personalized Advertising');

    return (
        <>
        <Navigator activeNav={props.activeNav} setActiveNav={props.setActiveNav} />
        <div className='fadeIn '>
          <WorkTitle 
            title={titleEn} 
            description={descriptionEn} 
            team={teamEn} 
            role={roleEn} 
            period={'2025.07. - 2025.09.'}
            titleKo={titleKo}
            descriptionKo={descriptionKo}
            teamKo={teamKo}
            roleKo={roleKo}
          />
        </div>
        <MainImage image={mainImage}/>
        <WorkTextSection 
            title={<div>Overview</div>} 
            description={<div>
                QR Order is a table-ordering solution that allows customers in offline restaurants and cafés to scan a QR code placed on the table or counter with their own mobile phones, browse the menu, and place and pay for orders directly.
                Karrot’s QR Order team was formed in June 2025, and the product made its market debut in August 2025.
            </div>}
            subDescription={<div>
                QR오더는 오프라인 음식점 및 카페 매장에서 개인 휴대폰으로 매장 테이블 및 카운터에 비치된 QR을 스캔하여 메뉴를 탐색하고, 직접 주문 및 결제를 할 수 있는 테이블오더 솔루션입니다.
                당근의 QR오더는 2025년 6월에 팀 세팅에 착수하여, 2025년 8월에 시장에 첫 출시를 했습니다. 
            </div>}
        />
        <WorkTextSection 
            title={<div>Background</div>} 
            description={<div>
                As of 2025, the kiosk and table-order market in Korea is experiencing rapid growth, driven by trends in automation and digital transformation.
                <br/>Karrot has been expanding its influence online by offering region-based business solutions through Business Profiles and local business advertising. However, its presence in offline local markets — the primary touchpoints of small business owners — has remained minimal, making it difficult for Karrot’s business impact to be fully recognized.
                <br/>Recognizing the need to establish a stronger presence in the offline scene, Karrot decided to begin its entry there, starting with <b>QR Order</b>, a solution designed to seamlessly connect the online and offline experiences in the F&B market.
                </div>}
            subDescription={<div>
                2025년 현재, 한국의 키오스크 및 테이블오더 시장은 무인화와 디지털 전환 트렌드에 힘입어 빠르게 성장하고 있습니다.
                <br/>당근은 비즈프로필과 소상공인 광고를 통해 지역 기반의 온라인 비즈니스 솔루션을 제공하며, 온라인 상에서 영향력을 확장해 나가고 있습니다. 그러나 동네 상권의 주요 활동 무대인 오프라인에서는 당근의 존재감이 미미해, 비즈니스 영향력을 체감하기 어렵다는 한계가 있습니다.
                <br/>이에 따라 당근은 오프라인 씬으로의 본격적인 진출 필요성을 인식하게 되었고, 그 첫 단계로 F&B 시장에서 온라인과 오프라인을 손쉽게 연결할 수 있는 <b>QR오더</b>를 선택했습니다.
            </div>}
        />
        <WorkFullImage image={getImage('background1')}/>
        <WorkTextSection 
            title={<div>Vision</div>} 
            description={<div>
                QR Order envisions evolving beyond an ordering solution into an <b>integrated online–offline CRM</b>, combining offline data such as in-store visits and payments with online business activities including discovery, reviews, and online payments.
                </div>}
            subDescription={<div>
                QR오더는 주문 솔루션을 넘어, 오프라인에서 수집되는 매장 방문 및 결제 데이터와 온라인 비즈니스 활동(탐색, 발견, 리뷰, 온라인 결제 등)을 통합하는 <b>온·오프라인 통합 CRM</b>으로 발전해 나가는 비전을 가지고 있습니다.
                </div>}
        />
        <WorkImage image={getImage('vision1')} isMaxheight={true} isbottomPadding={false}/>
        <WorkTextSection 
            title={<div>Goal</div>} 
            description={<div>
                This project aimed to develop a minimal viable product (MVP) within a short timeframe to quickly test market responses and lay the groundwork for scaling QR Order.
                Accordingly, the integration between online and offline experiences was set aside for this phase, and product development focused solely on ensuring that <b>in-store customers could seamlessly place and complete orders through QR Order without any errors.</b></div>}
            subDescription={<div>
                이 프로젝트는 짧은 기간 안에 최소 기능 제품(MVP)을 개발하여 빠르게 시장의 반응을 확인하고, QR오더가 확장할 수 있는 기반을 마련하는 것을 목표로 했습니다.
                이에 온라인과 오프라인의 연결 기능은 우선 제외하고, <b>매장 방문 고객이 QR오더를 통해 주문부터 결제까지 오류 없이 이용할 수 있는 시나리오</b>에 집중해 제품을 개발했습니다.</div>}
                />
        <WorkImage image={getImage('goal1')} isMaxheight={true} isbottomPadding={false}/>
        <WorkTextSection 
            title={<div>Research</div>} 
            description={<div>
                To develop an MVP with an outstanding user experience, the team visited various restaurants and cafés that offer QR ordering and tablet ordering solutions. We tested multiple products firsthand and evaluated their UX.  <br/>
                In addition, we conducted interviews with business owners to identify the most essential and critical features of table ordering, and observed how customers use such systems in real offline settings to gain practical insights.
                <br/> <br/>
                Through this research, we identified several key requirements:  <br/>
                1. Even when customers at the same table browse menus individually on their own phones, <b>they should be able to see each other's intended orders, and orders must be combined and processed as a single group order rather than separately.</b>  <br/>
                2. Since customers at each table change frequently, <b>orders from previous guests and new guests must be clearly separated.</b>  <br/>
                3. <b>The ability to add special requests to menu items</b> is essential for certain types of restaurants.  <br/>
                4. As a significant number of foreign visitors dine in Korea, <b>multilingual translation support</b> is essential.  <br/>
                </div>}
            subDescription={<div>
                뛰어난 UX를 갖춘 MVP를 개발하기 위해, QR오더 및 태블릿오더를 제공하는 다양한 식당과 카페를 직접 방문하여 여러 제품을 체험하고 UX를 평가했습니다.
                <br/>
                또한 매장 운영자와의 인터뷰를 통해 테이블오더에서 가장 필수적이고 중요한 기능을 파악했으며, 실제 오프라인 환경에서 고객들이 테이블오더를 사용하는 과정을 관찰하여 주요 인사이트를 도출했습니다.
                <br/><br/>
                조사 결과, 다음과 같은 핵심 요구사항을 확인했습니다: <br/>
                1. 테이블에 앉은 고객들이 각자의 휴대폰으로 메뉴를 보더라도, 함께 주문하는 고객이 서로 주문하려는 메뉴를 알 수 있어야 하며, 주문은 따로 전달되지 않고 한 번에 모아서 이루어져야 한다. <br/>
                2. 테이블에 앉는 고객은 계속 바뀌기 때문에, 이전 고객의 주문과 다음 고객의 주문이 명확히 구분되어야 한다. <br/>
                3. 주문 시 요청사항을 입력할 수 있는 기능은 일부 매장에서 필수적으로 필요하다. <br/> 
                4. 한국을 방문하는 외국인 고객이 많기 때문에, 다국어 번역 기능이 반드시 제공되어야 한다. <br/>
        </div>}
        />
       <WorkFullImage image={getImage('research1')} isBottomMargin={true} />
      <div style={{background: 'linear-gradient(180deg, var(--color-bg-soft) 70%, var(--color-bg-base) 100%)', paddingBottom:'60px'}}>
       <WorkTextSection
        title='Userflow'
        description={<div>Based on insights gained from our research, we designed order flows that support both <b>prepaid</b> and <b>postpaid</b> ordering scenarios.</div>}
        subDescription={<div>리서치를 통해 얻은 인사이트를 바탕으로, <b>선결제 주문</b>과 <b>후결제 주문</b> 시나리오를 모두 고려한 사용자 플로우를 설계했습니다.</div>}
        />
       <WorkFullImage image={getImage('research2')}/>
       </div>
       {/* <WorkTextSection
        title='UXUI Design'
        /> */}
        <WorkFullImage image={getImage('userflow1')} />
        <Divider/>
        <WorkFullImage image={getImage('userflow2')} />
        <WorkTextSection
        title='Key Design1. Easy-to-navigate UX'
        description={<div>I designed the structure to simplify the menu exploration process as much as possible, focusing on enabling users to browse various menu items effortlessly. I researched the most suitable gestures and incorporated them into the UX design.</div>}
        subDescription={<div>메뉴 탐색 과정을 최대한 단순화한 구조로 설계했으며, 사용자가 불편함 없이 다양한 메뉴를 살펴볼 수 있는 것에 초점을 맞춰, 가장 적합한 제스처를 연구했고, 이를 반영한 UX를 디자인했습니다.</div>}
        />
        <WorkFullImage image={getImage('design11')}/>
        <WorkFullImage image={getImage('design12')}/>
        <WorkTextSection
        title='Key Design2. Thoughtfully Designed Group Ordering UX'
        description={<div>
            Since orders are placed per table in the store, there may be a single user or multiple users who each scan the QR code to join the same session. Therefore, it was important to carefully design the shared ordering and payment experience to ensure smooth collaboration among users.
            <br/><br/>When another user adds or removes a menu item, color-coded bubble messages are used to provide feedback without obstructing the screen, allowing users to easily notice menu changes.
            <br/><br/>Additionally, when another user initiates a payment, the system clearly indicates that payment is in progress while still allowing users to freely browse and add new items. This design ensures a seamless and intuitive experience—similar to choosing menu items together in an offline setting.
</div>}
        subDescription={<div>
            매장에서는 테이블별로 주문이 이루어지기 때문에, 사용자가 한 명일 수도 있지만 여러 명이 각자 QR을 스캔하여 동일한 세션에 참여할 수도 있습니다. 따라서, 함께 주문 내역을 공유하고 결제하는 과정을 세심하게 디자인할 필요가 있었습니다.
            <br/><br/>다른 사용자가 메뉴를 추가하거나 제거할 때, 컬러가 구분된 버블 메시지를 활용해 화면을 가리지 않으면서도 메뉴 변경이 발생했음을 인지할 수 있도록 피드백을 제공했습니다.
            <br/><br/>또한 다른 사용자가 결제를 진행할 경우, 결제 중임을 명확히 알리면서도 사용자가 여전히 메뉴를 탐색하거나 새로 추가할 수 있도록 설계하여, 오프라인에서 메뉴를 고르는 것과 유사한 자연스러운 경험을 제공했습니다.
            </div>}
        />
        <WorkVideo video={design21Video} />
        <WorkVideo video={design24Video} />
        <WorkTextSection
        description={<div>
            Unlike mobile environments where users access services directly, in offline stores the experience begins when a customer scans a QR code.
            <br/>Therefore, it is crucial for the system to <b>accurately recognize who the user is.</b>
            <br/>When the same user scans the QR code again, the system should identify them as the same user and maintain their history. It must also determine whether people who scan the same QR are dining companions or unrelated individuals.
            <br/>If they are ordering together, they should be able to view shared cart items, order information, and payment history. However, if they are not companions, access to order details and payment history should be restricted.
            <br/>Additionally, when a new customer sits at the table after an order is completed, the system must distinguish them as a different user from the previous one and provide a completely new session.
</div>}
        subDescription={<div>
            모바일 환경과 달리, 오프라인 매장에서는 사용자가 QR 코드를 스캔하는 것으로 서비스가 시작되기 때문에, <b>해당 사용자가 누구인지 정확히 인식하는 것이 매우 중요합니다.</b>
            <br/>시스템은 같은 사용자가 다시 QR을 스캔했을 때 동일한 유저임을 인식하여 히스토리를 유지해 주어야 하며, 동일한 QR을 스캔한 사람들이 함께 식사를 하는 동석자인지, 함께 식사를 하지 않는 타인인지 파악해야 합니다. <br/>
            함께 주문을 한다면, 장바구니에 담은 메뉴, 주문한 메뉴 정보, 결제 내역 등을 함께 확인할 수 있어야 하지만, 동석자가 아니라면 주문한 메뉴나 결제 내역을 보지 못하도록 막아야 합니다. 
            <br/>또한 주문이 완료된 후 새로운 고객이 자리에 앉으면, 이전 고객과는 다른 새로운 사용자임을 구분하여, 완전히 새로운 세션을 제공해야 합니다. 
            </div>}
        />
        
        <WorkTextSection
        description={<div>
            As such, the system operates based on a session separation policy as follows:
            <br/>1. Scanning the QR code starts a new session for the corresponding store-table and issues a unique UserID for each device browser.
            <br/>2. Users are treated as a group (companions) if they are in the same page session during payment, or if they have added at least one item to the cart in the same page session, even if not present during payment. These users share the cart and order history.
            <br/>3. For postpaid stores, the session remains active while the table is occupied until the meal ends. However, for prepaid stores, if the QR is scanned again before the session expires by someone who doesn't meet the companion criteria, a new page session is created instead of joining the existing one.
            <br/>4. Sessions last for 15 minutes for prepaid stores and 8 hours for postpaid stores (to allow for additional orders). Once a session expires, all order history is automatically deleted and hidden from the user.
      </div>}
        subDescription={<div>
            따라서, 아래와 같이 상황에 따라 세션을 분리하는 정책으로 QR을 스캔한 유저의 상황에 맞는 주문 페이지를 제공할 수 있도록 했습니다:
            <br/>1. QR을 스캔하면 해당 매장-테이블의 새로운 Session이 시작되고, 기기 브라우저별로 고유한 UserID를 발급합니다. 
            <br/>2. 결제를 하는 순간 함께 동일한 페이지 Session에 진입해있던 User, 결제 순간에 함께 동일한 페이지 Session에 진입하지 않더라도 동일한 페이지 Session에 메뉴를 하나라도 추가한 User는 동석자로 취급하여, 장바구니, 주문내역 등의 정보를 공유합니다. 
            <br/>3. 후불 매장의 경우 음식을 모두 먹기 전까지 고객이 테이블을 점유하고 있기 때문에 세션을 만료하지 않지만, 선불 매장의 경우에는 음식을 모두 먹고 나갔는지 알 수 없기 때문에 Session이 만료되기 전 동일한 QR을 스캔하더라도 동석자의 기준에 부합하지 않으면 다른 페이지 Session을 제공합니다.
            <br/>4. Session이 시작되면 선불매장의 경우 15분, 후불 매장의 경우에는 추가 주문을 계속 할 수 있기 때문에 8시간 동안 유지하며, Session이 종료되면 주문내역은 자동으로 삭제되어 더 이상 사용자에게 노출되지 않습니다.
            </div>}
        />
        <WorkFullImage image={getImage('design22')}/>
        <WorkFullImage image={getImage('design23')}/>
        
        <WorkTextSection
        title='Key Design3. Language Support for Foreign Users'
        description={<div>
            As confirmed in interviews with business owners, running a one-person shop made it highly challenging to cook while simultaneously serving foreign customers. Therefore, for QR ordering to fully replace in-person order handling, translation functionality was essential. To address this, we implemented automatic translation into <b>Japanese, Chinese, and English</b> based on the user’s device settings, catering to the majority of foreign tourists visiting Korea.
        </div>}
        subDescription={<div>
            사업주 인터뷰에서 확인했듯이, 1인 운영 가게에서는 요리를 하면서 동시에 외국인 고객을 응대하는 일이 매우 큰 부담이었습니다. 따라서 QR오더가 주문 응대를 완전히 대체하기 위해서는 번역 기능이 반드시 필요했습니다. 한국을 방문하는 주요 외국인 관광객들을 위해, 사용자의 디바이스 설정에 따라 자동으로 <b>일본어, 중국어, 영어</b>로 번역되는 기능을 제공해 점주의 부담을 최소화했습니다.
            </div>}
        />
        <WorkFullImage image={getImage('design31')}/>
        <WorkTextSection 
        title='Prototype'
        description={<div>
            You can experience the service by scanning or tapping the QR code, just like placing an order at a real store.
            </div>}
        subDescription={<div>
            실제 매장에서 주문하듯 QR코드를 스캔하거나 눌러 서비스를 이용해볼 수 있습니다. </div>}
        />
        <a href="https://qr-order.daangn.com/7qpB5vp7HPi/menu?" target="_blank" rel="noopener noreferrer">
          <WorkImage image={getImage('prototype1')} isMaxheight={true} isbottomPadding={false}/>
        </a>
        <WorkImageGroup 
            images={[getImage('imp1'), getImage('imp2'), getImage('imp3')]} 
            description="QR Code Placement Mockup" 
        />
        <Divider/>
    
        <WorkTextSection
        title='On-site Service Implementation'
        description={<div>
            We rapidly developed our product within a month and installed the new QR Order system in two stores. <br/>
            One store operated on a <b>prepaid model</b>, and the other on a <b>postpaid model</b>, allowing us to gain diverse insights from real customer interactions in authentic environments.
            <br/>
            Customers visiting the stores easily located the QR codes on their tables, scanned them without difficulty, and completed the entire ordering and payment process within about two minutes.
            <br/>
            However, we discovered various obstacles customers faced during the payment stage. For example, students were unable to complete online payments because they used their parents’ cards instead of their own,while corporate card users often wished to split payments by amount, which our current system did not support.
            <br/>
            Based on these insights,we have established a plan to create an <b>inclusive payment environment</b> that accommodates all types of customers.
            </div>}
        subDescription={<div>
            우리 팀은 약 한 달 만에 제품을 빠르게 개발하여 두 개의 매장에 새로운 QR오더 제품을 설치했습니다.<br/>
            하나는 <b>선결제 매장</b>, 다른 하나는 <b>후결제 매장</b>으로 운영되었으며, 이를 통해 실제 고객이 사용하는 환경에서 다양한 인사이트를 얻을 수 있었습니다.
            <br/>
            매장을 방문한 고객은 테이블 위의 QR코드를 쉽게 찾아 별다른 어려움 없이 스캔했고, 약 2분 이내에 주문부터 결제까지 모든 과정을 마칠 수 있었습니다.
            <br/>
            그러나 결제 단계에서 고객마다 다양한 제약이 존재한다는 사실을 발견했습니다. 예를 들어, 학생의 경우 본인 명의가 아닌 부모님의 카드를 사용해 온라인 결제가 불가능했고, 법인카드 이용자는 금액별로 분할 결제를 원했지만 시스템에서 이를 지원하지 않아 결제를 완료하지 못했습니다.
            <br/>
            우리는 이러한 인사이트를 바탕으로, 모든 고객이 불편함 없이 결제할 수 있도록 <b>모든 결제 환경을 고려한 디자인</b>을 구축하기 위한 계획을 수립했습니다.
                        </div>}
        />
        <WorkImageGroup 
            images={[getImage('imp4'), getImage('imp5')]} 
            description="The QR codes installed in each store" 
        />
        
        <WorkTextSection
        title='Next Steps'
        description={<div>
            After launching the MVP, we quickly gained valuable insights and began planning the next steps to realize our business value.<br/>
        Going forward, Karrot QR Order will strengthen its integration with Karrot Business Tools, evolving into a comprehensive CRM solution that bridges both online and offline channels.<br/>
        Through this integration, we plan to connect activities on the <b>Karrot online platform</b>—such as store discovery, coupon issuance, and reviews—with <b>in-store customer order and payment data.</b>
        </div>}
        subDescription={<div>
            MVP를 런칭한 후, 우리는 빠르게 인사이트를 얻었고 비즈니스 가치를 실현하기 위해 다음 단계를 계획했습니다.<br/>
당근 QR오더는 앞으로 당근 비즈니스 도구와의 연계를 강화하여, 온·오프라인을 아우르는 CRM 도구로 발전할 예정입니다.<br/>
이를 통해 <b>당근 온라인 플랫폼</b>에서 활발하게 이루어지는 매장 탐색, 쿠폰 발급, 후기 작성과 같은 활동을 <b>실제 매장 방문 고객의 주문 및 결제 데이터</b>와 연결할 계획입니다.
        </div>}
        />
        <WorkImage image={getImage('next1')} isMaxheight={false} isbottomPadding={false} />
        <NextItem to='/Work16' thumbnail={thumbnail16} nextProjectTitle='QR Order Update'/>
        <BackButton/>
        <div style={{ height: "400px" }} />
        </>
    )
}

export default Work15;
